'use client';

import { Box, HStack } from '@terra/ui-styled-system';
import { ExecutionNeoIcon, IconButton, Tooltip } from '@terra/ui-design-system';
import {
  Clock01Icon,
  Loading03Icon,
  Message01Icon,
  Tick01Icon,
} from '@terra/ui-icons';
import type { GroupedSignal } from '@terra/shared-contracts-signals';
import { useAppTargetId } from '../../../store/projectStore';
import { useSyncNeoAttackFromTraces } from '../../../hooks/useSyncNeoAttackFromTraces';
import { ArchivedActionGate } from '../../common/ArchivedActionGate';
import { rowClickBlockerProps } from '../rowClickBlocker';
import {
  SIGNAL_NEO_STATUS,
  type SignalNeoStatus,
} from './signal-neo-status.model';
import { useSignalRowActions } from './useSignalRowActions';
import { useSignalsTableContext } from './SignalsTableContext';

const ACTION_ICON_SIZE = 12;

export function SignalNeoStatusIcon({ status }: { status: SignalNeoStatus }) {
  if (status === SIGNAL_NEO_STATUS.QUEUED) {
    return <Clock01Icon size={ACTION_ICON_SIZE} />;
  }

  if (status === SIGNAL_NEO_STATUS.RUNNING) {
    return (
      <Box animation="spin 1s linear infinite" display="flex">
        <Loading03Icon size={ACTION_ICON_SIZE} />
      </Box>
    );
  }

  if (status === SIGNAL_NEO_STATUS.COMPLETED) {
    return <Tick01Icon size={ACTION_ICON_SIZE} />;
  }

  return <ExecutionNeoIcon size={ACTION_ICON_SIZE} />;
}

export function SignalActionsCell({ signal }: { signal: GroupedSignal }) {
  const appTargetId = useAppTargetId();
  const { boothAttackSignalId } = useSignalsTableContext();
  const actions = useSignalRowActions(signal);

  useSyncNeoAttackFromTraces({
    signalId: signal.id,
    applicationId: signal.applicationId || appTargetId,
    traces: signal.traces,
  });

  return (
    <HStack gap={1} {...rowClickBlockerProps}>
      <Tooltip title={actions.neoTooltip}>
        <Box display="inline-flex" flexShrink={0}>
          <IconButton
            size="xs"
            variant="ghost"
            colorScheme={
              actions.neoStatus === SIGNAL_NEO_STATUS.COMPLETED
                ? 'green'
                : 'purple'
            }
            disabled={actions.isNeoDisabled}
            stopPropagation
            aria-label={actions.neoLabel}
            onClick={actions.startNeo}
          >
            <SignalNeoStatusIcon status={actions.neoStatus} />
          </IconButton>
        </Box>
      </Tooltip>
      <ArchivedActionGate
        isArchived={actions.isArchived}
        tooltip={actions.archivedTooltip}
      >
        <Tooltip title={actions.copilotLabel}>
          <Box
            display="inline-flex"
            data-tour-id={
              boothAttackSignalId === signal.id
                ? 'attack-with-copilot'
                : undefined
            }
          >
            <IconButton
              size="xs"
              variant="ghost"
              colorScheme="red"
              stopPropagation
              aria-label={actions.copilotLabel}
              onClick={actions.sendToCopilot}
            >
              <Message01Icon size={ACTION_ICON_SIZE} />
            </IconButton>
          </Box>
        </Tooltip>
      </ArchivedActionGate>
    </HStack>
  );
}
